import React, { useState, useEffect } from "react";
import BasicImg from "../assets/images/information.png";
import { Input } from "antd";
import RegularImg from "../assets/images/regular.png";
import StandardImg from "../assets/images/standard.png";
import PremiumImg from "../assets/images/premium.png";
import { MdOutlineSendToMobile } from "react-icons/md";
import {
  AiOutlineLoading3Quarters,
} from "react-icons/ai";
import axios, { formToJSON } from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import VerificationsHistoryTable from "./History/NINVerificationHistory.jsx";
import LogoImg from "../../assets/images/Logo.png";
import { config } from "../../config/config.jsx";
import CryptoJS from "crypto-js";
import Swal from "sweetalert2";

function NIN() {
  const navigate = useNavigate();

  /* ---------------------------------- data --------------------------------- */
  const cardVerify = [
    { label: "NIN", value: "nin" },
    { label: "Phone", value: "phone" },
  ];

  // Add new state for API prices
  const [AgentPrices, setAgentPrices] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
        
        const data = Array.isArray(response.data) ? response.data : [response.data];

        const ninPricing = data.find((item) => item.key === "nin");
        const ninPremiumPricing = data.find((item) => item.key === "nin_premium");

        if (ninPricing && ninPricing.prices) {
          // Update cardSlip with new prices
          const updatedCardSlip = cardSlip.map((slip) => {
            if (slip.value === "premium" && ninPremiumPricing && ninPremiumPricing.prices) {
              return {
                ...slip,
                price: ninPremiumPricing.prices.agent,
              };
            }
            return {
              ...slip,
              price: ninPricing.prices.agent,
            };
          });

          setAgentPrices(updatedCardSlip);
        }
      } catch (error) {
        toast.error("Failed to fetch current prices");
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  const cardSlip = [
    {
      label: "Information Slip",
      value: "basic",
      image: BasicImg,
      price: AgentPrices?.find((p) => p.value === "basic")?.price || 1,
    },
    {
      label: "Regular Slip",
      value: "regular",
      image: RegularImg,
      price: AgentPrices?.find((p) => p.value === "regular")?.price || 1,
    },
    {
      label: "Standard Slip",
      value: "standard",
      image: StandardImg,
      price: AgentPrices?.find((p) => p.value === "standard")?.price || 1,
    },
    {
      label: "Premium Slip",
      value: "premium",
      image: PremiumImg,
      price: AgentPrices?.find((p) => p.value === "premium")?.price || 1,
    },
  ];

  /* ---------------------------- component state ---------------------------- */
  const [selectedVerify, setSelectedVerify] = useState(""); // unselected by default
  const [selectedSlip, setSelectedSlip] = useState(""); // unselected by default
  const [formData, setFormData] = useState({
    nin: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  // Add your secret key for decryption
  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  /* --------------------------------- render -------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedVerify || !selectedSlip) {
      toast.error("Please select both verification type and slip layout");
      return;
    }

    if (
      selectedVerify === "phone" &&
      (!formData.phone || formData.phone.length !== 11)
    ) {
      toast.error("Please enter a valid 11-digit phone number");
      return;
    }

    if (
      (selectedVerify === "nin" || selectedVerify === "vnin") &&
      (!formData.nin || formData.nin.length !== 11)
    ) {
      toast.error("Please enter a valid 11-digit NIN");
      return;
    }

    // Find the selected slip's price from the updated prices
    const selectedSlipObj =
      AgentPrices?.find((s) => s.value === selectedSlip) ||
      cardSlip.find((s) => s.value === selectedSlip);
    const slipAmount = selectedSlipObj ? selectedSlipObj.price : 0;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Confirm Verification",
      text: `Are you sure you want to proceed with this verification? ${
        slipAmount > 0 ? `Amount: ₦${slipAmount}` : ""
      }`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, verify",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    // Get userId from encrypted localStorage
    let userId = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = decryptData(userStr);
        userId = userObj?._id || userObj?.id;
      }
    } catch {}

    setLoading(true);
    const payload = {
      verifyWith: selectedVerify,
      slipLayout: selectedSlip,
      amount: slipAmount,
      userId,
    };

    if (selectedVerify === "phone") {
      payload.phone = formData.phone;
    } else {
      payload.nin = formData.nin;
    }

    try {
      // Use different endpoint for basic NIN verification
      const endpoint =
        selectedSlip === "basic"
          ? `${config.apiBaseUrl}${config.endpoints.BasicNINVerification}`
          : `${config.apiBaseUrl}${config.endpoints.NINVerify}`;

      const response = await axios.post(endpoint, payload, {
        withCredentials: true,
      });
      

      setVerificationResult(response.data);

      // Check if data is actually returned for basic slip before showing success
      let ninData = null;
      if (selectedSlip === "basic") {
        ninData = response.data?.data?.nin_data || response.data?.data?.data || response.data?.data?.user_data?.nin_data || response.data?.data?.user_data;
        if (!ninData) {
          toast.error("Verification succeeded but no NIN data returned.");
          return; // Stop execution, don't show success modal or navigate
        }
      }

      // Show success alert
      await Swal.fire({
        title: "Verification Successful!",
        text: "Your NIN has been successfully verified.",
        icon: "success",
        confirmButtonColor: "#f59e0b",
      });

      // Navigate based on slip type
      if (selectedSlip === "basic") {
        // Basic slip - navigate to existing NIN slip component
        navigate("/dashboard/verifications/ninslip", {
          state: { userData: ninData },
        });
      } else if (selectedSlip === "regular") {
        // Regular, Standard, or Premium slip - navigate to new PDF component
        const slipTypeName = "regular";
        navigate("/dashboard/verifications/regularslip", {
          state: {
            responseData: response.data,
            slipType: slipTypeName,
          },
        });
      } else if (selectedSlip === "standard") {
        // Regular, Standard, or Premium slip - navigate to new PDF component
        const slipTypeName = "standard";
        navigate("/dashboard/verifications/standardslip", {
          state: {
            responseData: response.data,
            slipType: slipTypeName,
          },
        });
      } else if (selectedSlip === "premium") {
        // Regular, Standard, or Premium slip - navigate to new PDF component
        const slipTypeName = "premium";
        navigate("/dashboard/verifications/premiumslip", {
          state: {
            responseData: response.data,
            slipType: slipTypeName,
          },
        });
      } else {
        return null;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Verification failed");
      
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">NIN Verification</h2>
          <p className="text-sm text-gray-500 mt-1">Verify your National Identity Number</p>
        </div>
        <form onSubmit={handleSubmit}>
          {/* ------------------------------- Step #1 ------------------------------- */}
          <p className="mt-4 text-[14px] font-medium text-gray-600 mb-2">1. Verify With</p>

        <div className="grid grid-cols-2 gap-4 p-4">
          {cardVerify.map(({ label, value }) => (
            <label
              key={value}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border p-6 text-center transition
      ${
        selectedVerify === value
          ? "border-sky-400 ring-2 ring-sky-300 shadow-lg"
          : "border-gray-200 shadow-md hover:shadow-lg"
      }`}
            >
              <input
                type="radio"
                name="verifyWith"
                value={value}
                checked={selectedVerify === value}
                onChange={() => setSelectedVerify(value)}
                className="sr-only" // Change from hidden to sr-only
                aria-label={`Select ${label}`} // Add aria-label for accessibility
                tabIndex={0} // Make it focusable
              />

              <h3 className="mb-4 text-lg font-semibold text-gray-600">
                {label}
              </h3>

              {/* visual radio indicator */}
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                  selectedVerify === value
                    ? "border-sky-400"
                    : "border-gray-300"
                }`}
              >
                {selectedVerify === value && (
                  <span className="h-2.5 w-2.5 rounded-full bg-sky-500"></span>
                )}
              </span>
            </label>
          ))}
        </div>

          <p className="mt-7 text-[14px] font-medium text-gray-600 mb-2">2. Slip Layout</p>
          <div className="grid gap-6 p-4 sm:grid-cols-2 md:grid-cols-3">
          {cardSlip.map(({ label, value, image, price }) => (
            <label
              key={value}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border p-6 text-center transition
            ${
              selectedSlip === value
                ? "border-sky-400 ring-2 ring-sky-300 shadow-lg"
                : "border-gray-200 shadow-md hover:shadow-lg"
            }`}
            >
              <input
                type="radio"
                name="slipLayout"
                value={value}
                checked={selectedSlip === value}
                onChange={() => setSelectedSlip(value)}
                className="hidden"
                required
              />

              {/* price */}
              <p className="mb-4 text-3xl font-bold tracking-wide text-slate-700">
                {priceLoading ? "Loading..." : `₦${price}.00`}
              </p>

              {/* preview image */}
              <img
                src={image}
                alt={label}
                loading="lazy"
                className="mb-4 h-24 w-full object-contain"
              />

              {/* label */}
              <h4 className="mb-4 text-base font-semibold text-gray-600">
                {label}
              </h4>

              {/* visual radio indicator */}
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                  selectedSlip === value ? "border-sky-400" : "border-gray-300"
                }`}
              >
                {selectedSlip === value && (
                  <span className="h-2.5 w-2.5 rounded-full bg-sky-500"></span>
                )}
              </span>
            </label>
          ))}
        </div>
        {/* ------------------------------- Step #3 ------------------------------- */}
          <p className="mt-7 text-[14px] font-medium text-gray-600 mb-2">3. Supply ID Number</p>
          {(selectedVerify === "nin" || selectedVerify === "vnin") && (
            <input
              type="text"
              className="pl-4 py-3 border border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 rounded-lg w-full outline-none transition-all"
              placeholder="NIN NUMBER"
              required
              name="nin"
              value={formData.nin}
              onChange={handleInputChange}
              inputMode="numeric"
              maxLength="11"
              pattern="\d{11}"
              autoComplete="off"
              title="NIN must be exactly 11 digits"
            />
          )}
          {selectedVerify === "phone" && (
            <input
              type="text"
              className="pl-4 py-3 border border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 rounded-lg w-full outline-none transition-all"
              placeholder="PHONE NUMBER"
              required
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              inputMode="numeric"
              maxLength="11"
              pattern="\d{11}"
              autoComplete="off"
              title="Phone must be exactly 11 digits"
            />
          )}

          <p className="text-gray-400 text-xs mt-3 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            We'll never share your details with anyone else.
          </p>
          <label className="flex items-start mt-8 space-x-3 cursor-pointer">
            <span className="relative">
              <input
                type="checkbox"
                className="peer shrink-0 appearance-none h-5 w-5 border border-gray-400 rounded-sm bg-white checked:bg-blue-600 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                required
                title="Required"
              />
              <svg
                className="absolute w-4 h-4 text-white left-0.5 top-0.5 pointer-events-none hidden peer-checked:block"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.586l7.879-7.879a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              By checking this box, you agree that the owner of the ID has
              granted you consent to verify his/her identity.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className={`flex items-center text-xl mt-10 mb-8 cursor-pointer justify-center gap-2 ${
              loading ? "bg-gray-400" : "bg-sky-500 hover:bg-sky-600"
            } text-white font-medium py-2 px-4 rounded-xl w-full h-[50px] transition-colors`}
          >
            {loading ? (
              <AiOutlineLoading3Quarters className="animate-spin" />
            ) : (
              <MdOutlineSendToMobile className="" />
            )}
            {loading ? "Verifying..." : "Verify"}
          </button>
        </form>
        <ToastContainer />
      </div>

      {/* History Table */}
      <div className="w-full mt-10">
        <VerificationsHistoryTable />
      </div>
    </div>
  );
}

export default NIN;
